import { useCallback } from "react"

import { Handle, Position, useReactFlow } from "@xyflow/react"

import type { NodeProps } from "@xyflow/react"

import { Globe, X } from "@/components/icons"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Checkbox } from "@/components/ui/checkbox"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Textarea } from "@/components/ui/textarea"
import { CATEGORY_TONES } from "@/components/ui/tone-classes"
import { cn } from "@/lib/utils"

import { useNodeExecutionState } from "../../hooks/use-node-execution-state"
import { useWorkflowStore } from "../../stores/workflow-store"
import { NodeExecutionIndicator } from "../node-execution-indicator"
import { NodeOutputPopover } from "../node-output-popover"
import { NodeEditDialog } from "./node-edit-dialog"
import { ReferenceNodesPicker } from "./reference-nodes-picker"
import { TemplateField } from "./template-field"

export function HttpNode({ id, data, selected }: NodeProps) {
  const updateNodeData = useWorkflowStore((state) => state.updateNodeData)
  const { deleteElements } = useReactFlow()
  const { isExecuting, isCompleted, output } = useNodeExecutionState(id)

  const handleMethodChange = useCallback(
    (value: string) => {
      updateNodeData(id, { method: value })
    },
    [id, updateNodeData]
  )

  const handleHeadersChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      updateNodeData(id, { headers: e.target.value })
    },
    [id, updateNodeData]
  )

  const handleShowOutputChange = useCallback(
    (checked: boolean) => {
      updateNodeData(id, { showOutput: checked })
    },
    [id, updateNodeData]
  )

  const handleShowInActivityHistoryChange = useCallback(
    (checked: boolean) => {
      updateNodeData(id, { showInActivityHistory: checked })
    },
    [id, updateNodeData]
  )

  const handleDelete = useCallback(() => {
    void deleteElements({ nodes: [{ id }] })
  }, [id, deleteElements])

  const method = (data.method as string) || "GET"
  const hasOutput = isCompleted && !!output
  const renderEditor = (isModal = false) => (
    <div className="grid gap-3">
      <div className="grid grid-cols-3 gap-2">
        <div className="grid gap-1.5">
          <Label className="text-xs">Method</Label>
          <Select value={method} onValueChange={handleMethodChange}>
            <SelectTrigger className="nodrag h-7 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="GET">GET</SelectItem>
              <SelectItem value="POST">POST</SelectItem>
              <SelectItem value="PUT">PUT</SelectItem>
              <SelectItem value="PATCH">PATCH</SelectItem>
              <SelectItem value="DELETE">DELETE</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="col-span-2 grid gap-1.5">
          <Label className="text-xs">URL</Label>
          <TemplateField
            className="h-7 pr-8"
            placeholder="https://api.example.com/..."
            value={(data.url as string) || ""}
            onChange={(value) => updateNodeData(id, { url: value })}
          />
        </div>
      </div>
      <ReferenceNodesPicker
        currentNodeId={id}
        selectedNodeIds={(data.nodeReferences as string[] | undefined) ?? []}
        onChange={(ids) => updateNodeData(id, { nodeReferences: ids })}
      />
      <div className="grid gap-1.5">
        <Label className="text-xs">Headers (JSON)</Label>
        <Textarea
          className={cn("nodrag nowheel text-xs", isModal ? "min-h-[120px]" : "min-h-[40px]")}
          placeholder='{"Authorization": "Bearer ..."}'
          value={(data.headers as string) || ""}
          onChange={handleHeadersChange}
        />
      </div>
      {["POST", "PUT", "PATCH"].includes(method) && (
        <div className="grid gap-1.5">
          <Label className="text-xs">Body</Label>
          <TemplateField
            multiline
            className={cn("pr-8", isModal ? "min-h-[260px]" : "min-h-[40px]")}
            placeholder="Request body or JSON..."
            value={(data.body as string) || ""}
            onChange={(value) => updateNodeData(id, { body: value })}
          />
        </div>
      )}
      <div className="grid gap-2 border-t pt-2">
        <div className="flex items-center gap-2">
          <Checkbox
            checked={!!data.showOutput}
            className="nodrag"
            id={`${id}-showOutput${isModal ? "-modal" : ""}`}
            onCheckedChange={handleShowOutputChange}
          />
          <Label
            className="cursor-pointer text-xs font-normal"
            htmlFor={`${id}-showOutput${isModal ? "-modal" : ""}`}
          >
            Show Output
          </Label>
        </div>
        <div className="flex items-center gap-2">
          <Checkbox
            checked={data.showInActivityHistory !== false}
            className="nodrag"
            id={`${id}-showInActivityHistory${isModal ? "-modal" : ""}`}
            onCheckedChange={handleShowInActivityHistoryChange}
          />
          <Label
            className="cursor-pointer text-xs font-normal"
            htmlFor={`${id}-showInActivityHistory${isModal ? "-modal" : ""}`}
          >
            Show in Activity History
          </Label>
        </div>
      </div>
    </div>
  )

  return (
    <NodeOutputPopover disabled={!hasOutput} output={output || ""}>
      <Card
        className={cn(
          "relative min-w-[280px] border-2 shadow-md transition-[border-color,box-shadow] duration-200",
          data.active ? "border-success ring-success ring-2" : "border-border",
          selected && "ring-primary ring-offset-background ring-2 ring-offset-2",
          isExecuting && "workflow-node-executing",
          isCompleted && !isExecuting && "workflow-node-completed",
          hasOutput && "cursor-pointer"
        )}
      >
        <CardHeader className={cn("rounded-t-xl p-2", CATEGORY_TONES[0])}>
          <NodeExecutionIndicator
            hasOutput={hasOutput}
            isCompleted={isCompleted}
            isExecuting={isExecuting}
          />
          <CardTitle className="flex items-center justify-center gap-2 text-sm font-medium">
            <Globe className="size-4" />
            HTTP Request
            <NodeEditDialog
              description="Edit this HTTP request in a larger workspace."
              title="HTTP Request settings"
            >
              {renderEditor(true)}
            </NodeEditDialog>
          </CardTitle>
          {selected && (
            <Button
              className="nodrag absolute -top-2.5 -right-2.5 size-6 rounded-full p-0 shadow-md"
              size="icon"
              variant="destructive"
              onClick={handleDelete}
            >
              <X className="size-3" />
            </Button>
          )}
        </CardHeader>
        <CardContent className="p-3">
          {renderEditor()}
          <Handle position={Position.Left} type="target" />
          <Handle position={Position.Right} type="source" />
        </CardContent>
      </Card>
    </NodeOutputPopover>
  )
}
